import { Component, OnInit, OnDestroy, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CartService } from '../../../modules/products/services/cart.service';
import { CartEventService } from '../../services/cart-event.service';

@Component({
  selector: 'app-cart-badge',
  standalone: true,
  imports: [CommonModule, MatBadgeModule, MatButtonModule, MatIconModule],
  template: `
    <button class="cart-badge" mat-icon-button color="primary" [matBadge]="cartItemCount" 
            matBadgeColor="warn" [matBadgeHidden]="cartItemCount === 0"
            [class.pulse]="isPulsing"
            (click)="navigateToCart()">
      <mat-icon>shopping_cart</mat-icon>
    </button>
  `,
  styles: [`
    button {
      position: relative;
      height: 40px;
      width: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.3s ease;
    }
    .mat-badge-content {
      font-family: var(--body-font, 'Open Sans', sans-serif);
      font-weight: 600;
    }
    mat-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* Pulse animation when cart is updated */
    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.15);
      }
      100% {
        transform: scale(1);
      }
    }

    button.pulse {
      animation: pulse 0.6s ease-in-out;
    }

    /* Badge animation when count changes */
    ::ng-deep .mat-badge-content {
      transition: all 0.3s ease;
    }
  `]
})
export class CartBadgeComponent implements OnInit, OnDestroy {
  cartItemCount: number = 0;
  isPulsing: boolean = false;
  private destroy$ = new Subject<void>();
  private previousCount: number = 0;

  constructor(
    private cartService: CartService,
    private cartEventService: CartEventService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Subscribe to cart item count changes with animation
    this.cartService.cartItemCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        // Trigger pulse animation if count changed
        if (count !== this.previousCount && this.previousCount !== 0) {
          this.triggerPulseAnimation();
        }
        this.previousCount = count;
        this.cartItemCount = count;
      });

    // Listen to cart events for real-time updates
    this.cartEventService.cartAction$
      .pipe(takeUntil(this.destroy$))
      .subscribe(action => {
        // Refresh cart count when any cart action occurs
        this.cartService.loadCartFromAPI().subscribe();
        
        // Trigger pulse animation for visual feedback
        this.triggerPulseAnimation();
      });

    // Note: Cart is already loaded by CartService constructor
    // Only reload if the service might not have initialized yet
    if (this.cartItemCount === 0 && this.cartService.isUserLoggedIn()) {
      this.cartService.loadCartFromAPI().subscribe();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateToCart(): void {
    this.router.navigate(['/products/cart']);
  }

  /**
   * Trigger pulse animation for visual feedback
   */
  private triggerPulseAnimation(): void {
    this.isPulsing = true;
    // Remove animation class after animation completes
    setTimeout(() => {
      this.isPulsing = false;
    }, 600); // Match animation duration
  }
}
